// Third Party
import React, { useState, useEffect } from 'react';

// Namespaces
import * as ProposalNS from '@/modules/proposal/Proposal';

// Components
import DownloadPDFButton from './DownloadPDFButton';
import useScreenSize from '@/hooks/useScreenSize';
import EditProposalModal from './edit-proposal-modal/EditProposalModal';
import { SecondaryButton } from '@/views/components/buttons';

// Types
import User from '@/types/user';
import Locale from '@/types/locale';
import Comparison from '@/types/comparison';

// Utils
import { FormattedMessage } from '@/utils/i18n';
import ZendeskUtils from '@/utils/3rd-party/zendesk';
import { PERMISSIONS, userHasPermission } from '@/utils/permissions';
import { useSelector } from 'react-redux';
import { ReduxState } from '@/redux/modules/types';

type BottomToolboxProps = {
  user: User;
  template: ProposalNS.ProposalTemplateV2;
  proposal: ProposalNS.Proposal;
  comparison: Comparison;
  proposalTemplateOptions: ProposalNS.ProposalTemplateOptions;
  locale: Locale;
  downloadPdf?: () => void;
};

const BottomToolbox = ({ user, template, proposal, proposalTemplateOptions, downloadPdf, comparison, locale }: BottomToolboxProps) => {
  const { isMobile } = useScreenSize();
  const [open, setOpen] = useState(false);
  const gifHideBaseScenario = useSelector((state: ReduxState) => state.proposalV2.gifHideBaseScenario);

  // The widget would overlap on the save button on smaller resolutions.
  // It was decided at that point to hide the widget on that screen
  useEffect(() => {
    if (open) {
      ZendeskUtils.hideWidget();
    }
    return () => {
      if (userHasPermission(user, PERMISSIONS.CHAT_SUPPORT)) {
        ZendeskUtils.showWidget();
      }
    };
  }, [open, user]);

  return (
    <>
      <div className="mobile-toolbox-container" aria-label="Proposal toolbar">
        <SecondaryButton icon={isMobile ? '' : 'pencil'} compact className="edit-button" onClick={() => setOpen(true)}>
          <FormattedMessage id="Common.Edit" />
        </SecondaryButton>
        <DownloadPDFButton user={user} downloadPdf={downloadPdf} isMobile={isMobile} />
      </div>
      <EditProposalModal
        template={template}
        proposal={proposal}
        proposalTemplateOptions={proposalTemplateOptions}
        user={user}
        open={open}
        setOpen={setOpen}
        comparison={comparison}
        locale={locale}
        gifHideBaseScenario={gifHideBaseScenario}
      />
    </>
  );
};

export default BottomToolbox;
